export const GEMINI_OMNI_MODEL_KEYS = ['gemini-omni', 'gemini-omni-vip', 'gemini-omni-1.1-flash', 'gemini-omni-1.1-flash-vip'];

export const isGeminiOmniModel = (modelKey) => GEMINI_OMNI_MODEL_KEYS.includes(modelKey);

export const GEMINI_OMNI_VIP_RATES = { '360p': 40, '720p': 50, '1080p': 60, '4k': 100 };
export const GEMINI_OMNI_VIP_VIDEO_PRICES = { '360p': 480, '720p': 600, '1080p': 720, '4k': 1200 };

// Retail prices: video input is billed once, regardless of source/output duration.
export const getGeminiOmniCreditCost = ({ modelKey, resolution, duration = 6, hasVideoReference, functionMode } = {}) => {
  const tier = modelKey === 'gemini-omni-vip' ? '720p' : String(resolution || '720p').toLowerCase();
  const is4k = tier === '4k';
  const isVip = String(modelKey || '').endsWith('-vip');
  if (hasVideoReference || ['editing', 'extend'].includes(functionMode)) {
    return isVip ? (GEMINI_OMNI_VIP_VIDEO_PRICES[tier] ?? GEMINI_OMNI_VIP_VIDEO_PRICES['720p']) : (is4k ? 360 : 240);
  }
  const seconds = Number(duration);
  const rate = isVip ? (GEMINI_OMNI_VIP_RATES[tier] ?? GEMINI_OMNI_VIP_RATES['720p']) : (is4k ? 40 : 20);
  return (Number.isFinite(seconds) && seconds > 0 ? seconds : 6) * rate;
};

export const GEMINI_OMNI_VIP_MODEL_KEY = 'gemini-omni-vip';
export const GROK_IMAGINE_VIDEO_VIP_MODEL_KEY = 'grok-imagine-video-vip';
export const KLING_V3_OMNI_MODEL_KEY = 'kling-v3-omni';
export const HAPPYHORSE_VIDEO_EDIT_MODEL_KEY = 'happyhorse-1.0-video-edit';
export const GEMINI_OMNI_VIP_EDIT_FUNCTION_MODE = 'editing';
export const GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS = 6;
export const GEMINI_OMNI_VIP_CREDITS_PER_SECOND = GEMINI_OMNI_VIP_RATES['720p'];
export const GEMINI_OMNI_VIP_MIN_DURATION_SECONDS = 3;
export const GEMINI_OMNI_VIP_MAX_DURATION_SECONDS = 10;
export const GROK_IMAGINE_VIDEO_VIP_DEFAULT_DURATION_SECONDS = 6;
export const GROK_IMAGINE_VIDEO_VIP_CREDITS_PER_SECOND_BY_RESOLUTION = {
  '480p': 24,
  '720p': 32,
};
export const GROK_IMAGINE_VIDEO_VIP_MIN_BILLING_DURATION_SECONDS = 2;
export const GROK_IMAGINE_VIDEO_VIP_MAX_BILLING_DURATION_SECONDS = 9;
export const KLING_V3_OMNI_DEFAULT_DURATION_SECONDS = 5;
export const KLING_V3_OMNI_MIN_DURATION_SECONDS = 3;
export const KLING_V3_OMNI_MAX_DURATION_SECONDS = 10;
export const HAPPYHORSE_VIDEO_EDIT_DEFAULT_DURATION_SECONDS = 5;
export const HAPPYHORSE_VIDEO_EDIT_MIN_DURATION_SECONDS = 3;
export const HAPPYHORSE_VIDEO_EDIT_MAX_DURATION_SECONDS = 15;

const AUTO_EDIT_DURATION_MODELS = {
  'gemini-omni-1.1-flash-vip': { defaultDuration: 6, minDuration: 3, maxDuration: 10 },
  [GEMINI_OMNI_VIP_MODEL_KEY]: {
    defaultDuration: GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS,
    minDuration: GEMINI_OMNI_VIP_MIN_DURATION_SECONDS,
    maxDuration: GEMINI_OMNI_VIP_MAX_DURATION_SECONDS,
    creditsPerSecond: GEMINI_OMNI_VIP_CREDITS_PER_SECOND,
  },
  [GROK_IMAGINE_VIDEO_VIP_MODEL_KEY]: {
    defaultDuration: GROK_IMAGINE_VIDEO_VIP_DEFAULT_DURATION_SECONDS,
    minDuration: GROK_IMAGINE_VIDEO_VIP_MIN_BILLING_DURATION_SECONDS,
    maxDuration: GROK_IMAGINE_VIDEO_VIP_MAX_BILLING_DURATION_SECONDS,
    creditsPerSecondByResolution: GROK_IMAGINE_VIDEO_VIP_CREDITS_PER_SECOND_BY_RESOLUTION,
  },
  [KLING_V3_OMNI_MODEL_KEY]: {
    defaultDuration: KLING_V3_OMNI_DEFAULT_DURATION_SECONDS,
    minDuration: KLING_V3_OMNI_MIN_DURATION_SECONDS,
    maxDuration: KLING_V3_OMNI_MAX_DURATION_SECONDS,
  },
  [HAPPYHORSE_VIDEO_EDIT_MODEL_KEY]: {
    defaultDuration: HAPPYHORSE_VIDEO_EDIT_DEFAULT_DURATION_SECONDS,
    minDuration: HAPPYHORSE_VIDEO_EDIT_MIN_DURATION_SECONDS,
    maxDuration: HAPPYHORSE_VIDEO_EDIT_MAX_DURATION_SECONDS,
  },
};

const normalizePositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const getAutoEditDurationConfig = (modelKey) =>
  AUTO_EDIT_DURATION_MODELS[String(modelKey || '').trim().toLowerCase()] || null;

const clampAutoEditDuration = (seconds, config) => Math.min(
  config.maxDuration,
  Math.max(config.minDuration, seconds)
);

export const isGeminiOmniVipEditing = ({ modelKey, functionMode } = {}) =>
  String(modelKey || '').trim().toLowerCase() === GEMINI_OMNI_VIP_MODEL_KEY
  && String(functionMode || '').trim().toLowerCase() === GEMINI_OMNI_VIP_EDIT_FUNCTION_MODE;

export const isAutoVideoEditDurationMode = ({ modelKey, functionMode } = {}) =>
  Boolean(getAutoEditDurationConfig(modelKey)) &&
  String(functionMode || '').trim().toLowerCase() === GEMINI_OMNI_VIP_EDIT_FUNCTION_MODE;

export const getPrimaryVideoAssetDuration = (inputAssets = []) => {
  if (!Array.isArray(inputAssets)) return null;
  const primaryVideo = inputAssets.find((asset) => String(asset?.type || '').toLowerCase() === 'video');
  return normalizePositiveNumber(primaryVideo?.durationSeconds ?? primaryVideo?.duration);
};

export const resolveGeminiOmniVipEditBillingDuration = ({
  inputAssets = [],
  fallbackDuration = GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS,
} = {}) => {
  return resolveAutoVideoEditBillingDuration({
    modelKey: GEMINI_OMNI_VIP_MODEL_KEY,
    inputAssets,
    fallbackDuration,
  });
};

export const resolveAutoVideoEditBillingDuration = ({
  modelKey,
  inputAssets = [],
  fallbackDuration,
} = {}) => {
  const config = getAutoEditDurationConfig(modelKey);
  if (!config) return normalizePositiveNumber(fallbackDuration);

  const sourceVideoDuration = getPrimaryVideoAssetDuration(inputAssets);
  const fallback = normalizePositiveNumber(fallbackDuration) || config.defaultDuration;
  const roundedDuration = Math.round(sourceVideoDuration || fallback);
  return clampAutoEditDuration(roundedDuration, config);
};

const resolveCreditsPerSecond = ({ config, resolution } = {}) => {
  if (!config) return null;
  if (Number.isFinite(Number(config.creditsPerSecond))) return Number(config.creditsPerSecond);
  const resolutionKey = String(resolution || '').trim().toLowerCase();
  const resolutionCost = config.creditsPerSecondByResolution?.[resolutionKey];
  if (Number.isFinite(Number(resolutionCost))) return Number(resolutionCost);
  return null;
};

export const buildGeminiOmniVipEditBillingContext = ({
  modelKey,
  functionMode,
  inputAssets = [],
  selectedDuration,
} = {}) => {
  if (!isGeminiOmniVipEditing({ modelKey, functionMode })) return null;

  return buildAutoVideoEditBillingContext({
    modelKey,
    functionMode,
    inputAssets,
    selectedDuration,
    resolution: '720p',
  });
};

export const buildAutoVideoEditBillingContext = ({
  modelKey,
  functionMode,
  inputAssets = [],
  selectedDuration,
  resolution,
} = {}) => {
  const config = getAutoEditDurationConfig(modelKey);
  if (!config || !isAutoVideoEditDurationMode({ modelKey, functionMode })) return null;

  const sourceVideoDuration = getPrimaryVideoAssetDuration(inputAssets);
  const selectedDurationSeconds = normalizePositiveNumber(selectedDuration);
  const billableDuration = resolveAutoVideoEditBillingDuration({
    modelKey,
    inputAssets,
    fallbackDuration: selectedDurationSeconds || config.defaultDuration,
  });

  return {
    mode: 'auto',
    source: sourceVideoDuration ? 'input_video_duration' : 'default_duration',
    selectedDuration: selectedDurationSeconds,
    sourceVideoDuration,
    billableDuration,
    creditsPerSecond: isGeminiOmniModel(modelKey) ? null : resolveCreditsPerSecond({ config, resolution }),
    ...(isGeminiOmniModel(modelKey) ? { creditsPerRequest: getGeminiOmniCreditCost({ modelKey, resolution, hasVideoReference: true }) } : {}),
    pricingUnit: isGeminiOmniModel(modelKey) ? 'credits_per_video' : 'credits_per_second',
  };
};
